import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Fetch messages from Firestore in descending order by createdAt
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const messagesQuery = query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(messagesQuery);
        const messagesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(messagesData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load messages: ' + err.message);
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  // Handle clicking a message row to view details and mark as read
  const handleRowClick = async (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      try {
        const messageRef = doc(db, 'contactMessages', message.id);
        await updateDoc(messageRef, { read: true });
        // Update local state to reflect the change
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === message.id ? { ...msg, read: true } : msg
          )
        );
      } catch (err) {
        console.error('Error marking message as read:', err);
        setError('Failed to mark message as read: ' + err.message);
      }
    }
  };

  // Handle deleting a message
  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }
    try {
      const messageRef = doc(db, 'contactMessages', messageId);
      await deleteDoc(messageRef);
      // Update local state to remove the deleted message
      setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message: ' + err.message);
    }
  };

  const handleClosePopup = () => {
    setSelectedMessage(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading messages...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="messages p-6 font-['Noto Sans'] max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-600">Contact Messages</h2>
      {messages.length === 0 ? (
        <p className="text-gray-600">No messages found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-md border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="p-3 text-left">New</th> {/* New column for unread indicator */}
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Subject</th>
                <th className="p-3 text-left">Sent At</th>
                <th className="p-3 text-left">Actions</th> {/* New column for actions */}
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr
                  key={message.id}
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(message)}
                >
                  <td className="p-3">
                    {!message.read && (
                      <span className="inline-block w-4 h-4 bg-red-600 rounded-full" title="New message"></span>
                    )}
                  </td>
                  <td className="p-3">{message.name}</td>
                  <td className="p-3">{message.email}</td>
                  <td className="p-3">{message.subject}</td>
                  <td className="p-3">
                    {new Date(message.createdAt).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click from triggering
                        handleDelete(message.id);
                      }}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Popup for message details */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleClosePopup}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl relative max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-2xl font-bold"
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-4 text-gray-600">Message Details</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>Name:</strong> {selectedMessage.name}</p>
                <p><strong>Email:</strong> {selectedMessage.email}</p>
                <p><strong>Subject:</strong> {selectedMessage.subject}</p>
                <p><strong>Message:</strong> {selectedMessage.message}</p>
                <p><strong>Sent At:</strong> {new Date(selectedMessage.createdAt).toLocaleString()}</p>
                <p><strong>Status:</strong> {selectedMessage.read ? 'Read' : 'Unread'}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Messages;